import React, { useState } from 'react';
import { Star } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { submitEventRating } from '../../services/api/events';
import FingerprintJS from '@fingerprintjs/fingerprintjs';

export const StarRating = ({ eventId, currentRatingSum = 0, currentRatingCount = 0, onRate }) => {
  const [hoverRating, setHoverRating] = useState(0);
  const [hasRated, setHasRated] = useState(() => localStorage.getItem(`rated_${eventId}`) === 'true');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const { theme } = useTheme();

  const average = currentRatingCount > 0 ? (currentRatingSum / currentRatingCount).toFixed(1) : 0;

  const handleRate = async (rating) => {
    if (hasRated || isSubmitting) return;
    setIsSubmitting(true);
    setErrorMsg(null);

    try {
      // Gera fingerprint para evitar spam
      const fp = await FingerprintJS.load();
      const result = await fp.get();
      const visitorId = result.visitorId;

      await submitEventRating(eventId, rating, visitorId);

      localStorage.setItem(`rated_${eventId}`, 'true');
      setHasRated(true);
      
      const newSum = (currentRatingSum || 0) + rating;
      const newCount = (currentRatingCount || 0) + 1;
      
      if (onRate) onRate(newSum, newCount);
    } catch (err) {
      console.error("Error rating:", err);
      setErrorMsg(err.message || 'Erro ao enviar avaliação.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`flex flex-col items-center p-3 rounded-lg border ${theme === 'dark' ? 'bg-slate-900 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
      <div className="flex items-center justify-between w-full mb-2">
        <span className={`text-xs font-semibold ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>Avaliação Pública</span>
        <div className="flex items-center gap-1">
          <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" />
          <span className={`text-sm font-bold ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>{average}</span>
          <span className={`text-[10px] ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>({currentRatingCount})</span>
        </div>
      </div>

      <div className="flex gap-2">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            disabled={hasRated || isSubmitting}
            onMouseEnter={() => setHoverRating(star)}
            onMouseLeave={() => setHoverRating(0)}
            onClick={() => handleRate(star)}
            className={`p-1 transition-all ${hasRated ? 'cursor-default' : 'cursor-pointer hover:scale-110 active:scale-95'}`}
          >
            <Star
              className={`w-6 h-6 transition-all ${(hoverRating >= star || (hasRated && average >= star))
                  ? 'text-yellow-400 fill-yellow-400 drop-shadow-[0_0_8px_rgba(250,204,21,0.5)]'
                  : (theme === 'dark' ? 'text-slate-700 hover:text-slate-500' : 'text-slate-300 hover:text-slate-400')
                }`}
            />
          </button>
        ))}
      </div>
      {hasRated && <p className="text-[10px] text-emerald-500 mt-2 font-medium animate-in fade-in">Obrigado por avaliar!</p>}
      {!hasRated && !errorMsg && <p className={`text-[10px] mt-2 ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>Deixe sua nota para este evento</p>}
      {errorMsg && <p className="text-[10px] text-red-500 mt-2 font-medium animate-in fade-in">{errorMsg}</p>}
    </div>
  );
};
